import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  requestPasswordReset: (email) => api.post('/auth/password-reset-request', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/password-reset', { token, new_password: newPassword })
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getPending: () => api.get('/users/pending'),
  approve: (id) => api.put(`/users/${id}/approve`),
  update: (id, data) => api.put(`/users/${id}`, data),
  resetPassword: (id, newPassword) => api.put(`/users/${id}/password`, { new_password: newPassword }),
  delete: (id) => api.delete(`/users/${id}`)
};

// News API
export const newsAPI = {
  getAll: (site = null, publishedOnly = true) => {
    let url = `/news?published_only=${publishedOnly}`;
    if (site) url += `&site=${site}`;
    return api.get(url);
  },
  getOne: (id) => api.get(`/news/${id}`),
  create: (data) => api.post('/news', data),
  update: (id, data) => api.put(`/news/${id}`, data),
  delete: (id) => api.delete(`/news/${id}`)
};

// Gallery API
export const galleryAPI = {
  getAlbums: (site = null, publishedOnly = true) => {
    let url = `/gallery?published_only=${publishedOnly}`;
    if (site) url += `&site=${site}`;
    return api.get(url);
  },
  getAlbum: (id) => api.get(`/gallery/${id}`),
  createAlbum: (data) => api.post('/gallery', data),
  updateAlbum: (id, data) => api.put(`/gallery/${id}`, data),
  deleteAlbum: (id) => api.delete(`/gallery/${id}`),
  addImage: (albumId, data) => api.post(`/gallery/${albumId}/images`, data),
  deleteImage: (imageId) => api.delete(`/gallery/images/${imageId}`)
};

// Classes API
export const classesAPI = {
  getAll: (site = null, activeOnly = true) => {
    let url = `/classes?active_only=${activeOnly}`;
    if (site) url += `&site=${site}`;
    return api.get(url);
  },
  getOne: (id) => api.get(`/classes/${id}`),
  create: (data) => api.post('/classes', data),
  update: (id, data) => api.put(`/classes/${id}`, data),
  delete: (id) => api.delete(`/classes/${id}`)
};

// Menu API
export const menuAPI = {
  getAll: (site = null) => {
    let url = '/menu';
    if (site) url += `?site=${site}`;
    return api.get(url);
  },
  getCurrentWeek: (site = null) => {
    let url = '/menu/current-week';
    if (site) url += `?site=${site}`;
    return api.get(url);
  },
  create: (data) => api.post('/menu', data),
  update: (id, data) => api.put(`/menu/${id}`, data),
  delete: (id) => api.delete(`/menu/${id}`)
};

// Documents API
export const documentsAPI = {
  getAll: (site = null, category = null, publishedOnly = true) => {
    let url = `/documents?published_only=${publishedOnly}`;
    if (category) url += `&category=${category}`;
    if (site) url += `&site=${site}`;
    return api.get(url);
  },
  create: (data) => api.post('/documents', data),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`)
};

// Events API
export const eventsAPI = {
  getAll: (site = null, publishedOnly = true, upcomingOnly = false) => {
    let url = `/events?published_only=${publishedOnly}&upcoming_only=${upcomingOnly}`;
    if (site) url += `&site=${site}`;
    return api.get(url);
  },
  getOne: (id) => api.get(`/events/${id}`),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`)
};

// Contact API
export const contactAPI = {
  getInfo: (site) => api.get(`/contact-info/${site}`),
  updateInfo: (site, data) => api.put(`/contact-info/${site}`, data),
  sendMessage: (data) => api.post('/contact-messages', data),
  getMessages: (site = null) => {
    let url = '/contact-messages';
    if (site) url += `?site=${site}`;
    return api.get(url);
  },
  markRead: (id) => api.put(`/contact-messages/${id}/read`),
  deleteMessage: (id) => api.delete(`/contact-messages/${id}`)
};

// Pages API
export const pagesAPI = {
  getPage: (site, key) => api.get(`/pages/${site}/${key}`),
  updatePage: (site, key, data) => api.put(`/pages/${site}/${key}`, data)
};

// Dashboard API
export const dashboardAPI = {
  getStats: (site = null) => {
    let url = '/dashboard/stats';
    if (site) url += `?site=${site}`;
    return api.get(url);
  }
};

// Enrollment API
export const enrollmentAPI = {
  get: (site) => api.get(`/enrollment/${site}`),
  update: (site, data) => api.put(`/enrollment/${site}`, data)
};

// Maintenance API
export const maintenanceAPI = {
  get: () => api.get('/settings/maintenance'),
  update: (data) => api.put('/settings/maintenance', data)
};

// Registration Settings API
export const registrationAPI = {
  get: () => api.get('/settings/registration'),
  update: (data) => api.put('/settings/registration', data)
};

// Backup API
export const backupAPI = {
  create: () => api.post('/backup/create'),
  list: () => api.get('/backup/list'),
  download: (filename) => `${API_URL}/backup/download/${filename}`,
  restore: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/backup/restore', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  delete: (filename) => api.delete(`/backup/${filename}`),
  cleanupOrphaned: (dryRun = false) => api.post(`/maintenance/cleanup-orphaned-files?dry_run=${dryRun}`),
  migrateToGridFS: () => api.post('/maintenance/migrate-to-gridfs')
};

// Staff API
export const staffAPI = {
  getAll: (site = null, activeOnly = true) => {
    let url = `/staff?active_only=${activeOnly}`;
    if (site) url += `&site=${site}`;
    return api.get(url);
  },
  getOne: (id) => api.get(`/staff/${id}`),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  delete: (id) => api.delete(`/staff/${id}`)
};

// Upload API
export const uploadAPI = {
  upload: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Seed Data API
export const seedAPI = {
  seedData: () => api.post('/seed-data')
};

// Site constants
export const SITES = {
  MS: 'ms',
  DS: 'ds'
};

export const SITE_NAMES = {
  ms: 'MŠ Cestička',
  ds: 'Chaloupka na vršku'
};

export const SITE_COLORS = {
  ms: { primary: '#45AAF2', secondary: '#26DE81', gradient: 'from-[#45AAF2] to-[#26DE81]' },
  ds: { primary: '#FF6B6B', secondary: '#FF9F43', gradient: 'from-[#FF6B6B] to-[#FF9F43]' }
};

// Landing API
export const landingAPI = {
  get: () => api.get('/landing'),
  update: (site, data) => api.put(`/landing/${site}`, data)
};

// Theme API
export const themeAPI = {
  get: () => api.get('/settings/theme'),
  update: (theme, enabled) => api.put('/settings/theme', { theme, enabled })
};

// Analytics API
export const analyticsAPI = {
  track: (path, site = null) => api.post('/analytics/track', { path, site }).catch(() => {}),
  stats: (days = 30) => api.get(`/analytics/stats?days=${days}`)
};

// Výchozí konfigurace rozcestníku — slouží jako fallback, pokud API není dostupné
// (např. starší backend bez /api/landing endpointu)
export const LANDING_DEFAULTS = {
  ms: {
    icon_emoji: '🏫',
    icon_image_url: null,
    title: 'MŠ Cestička',
    subtitle: 'Mateřská škola pro děti 3-6 let',
    badges: [
      { label: '3 třídy', color_key: 'blue' },
      { label: 'Zahrada', color_key: 'green' },
      { label: 'Kroužky', color_key: 'orange' }
    ]
  },
  ds: {
    icon_emoji: '🏡',
    icon_image_url: null,
    title: 'Chaloupka na vršku',
    subtitle: 'Dětská skupina pro nejmenší',
    badges: [
      { label: 'Rodinné prostředí', color_key: 'red' },
      { label: 'Malý kolektiv', color_key: 'orange' }
    ]
  }
};

// Paleta barev pro štítky (badges) na rozcestníku — sdílena public + admin
export const BADGE_COLORS = {
  blue:   { bg: '#E8F4FD', text: '#45AAF2', label: 'Modrá' },
  green:  { bg: '#E6FFED', text: '#26DE81', label: 'Zelená' },
  orange: { bg: '#FFF5E6', text: '#FF9F43', label: 'Oranžová' },
  red:    { bg: '#FFE6E6', text: '#FF6B6B', label: 'Červená' },
  yellow: { bg: '#FFFBEA', text: '#F59E0B', label: 'Žlutá' },
  purple: { bg: '#F3E8FF', text: '#A855F7', label: 'Fialová' },
  pink:   { bg: '#FCE7F3', text: '#EC4899', label: 'Růžová' },
  teal:   { bg: '#CCFBF1', text: '#14B8A6', label: 'Tyrkysová' }
};

// Alergeny podle EU směrnice 1169/2011 (14 kódů)
export const ALLERGENS = [
  { code: '1',  label: 'Obiloviny s lepkem' },
  { code: '2',  label: 'Korýši' },
  { code: '3',  label: 'Vejce' },
  { code: '4',  label: 'Ryby' },
  { code: '5',  label: 'Arašídy' },
  { code: '6',  label: 'Sója' },
  { code: '7',  label: 'Mléko a mléčné výrobky' },
  { code: '8',  label: 'Skořápkové plody (ořechy)' },
  { code: '9',  label: 'Celer' },
  { code: '10', label: 'Hořčice' },
  { code: '11', label: 'Sezam' },
  { code: '12', label: 'Oxid siřičitý a siřičitany' },
  { code: '13', label: 'Vlčí bob (lupina)' },
  { code: '14', label: 'Měkkýši' }
];
export const ALLERGENS_BY_CODE = Object.fromEntries(ALLERGENS.map(a => [a.code, a]));

export default api;
